/**
 * City Agency Landing Pages - Wave 5
 * Adds: Beirut, Amman, Dar es Salaam, Kigali, Abidjan, Dakar, Bangalore, Jakarta, Santiago, Monterrey
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web apps, portals, dashboards. React, TypeScript, cloud-native.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'NLP, computer vision, predictive analytics, LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP. CI/CD, containers, monitoring, managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA, DevOps integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, design systems.' },
  { name: 'Cybersecurity', description: 'Pen testing, security audits, SOC, compliance (ISO 27001, GDPR, HIPAA).' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, omnichannel solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling for SaaS companies.' },
  { name: 'Real Estate & PropTech', description: 'Property management, virtual tours, tenant portals.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse systems.' },
];

export const CITY_AGENCY_PAGES_WAVE5: Record<string, CityAgencyData> = {

  'beirut': {
    slug: 'software-development-agency-beirut',
    cityName: 'Beirut',
    countryName: 'Lebanon',
    flag: '🇱🇧',
    timezone: 'EET (GMT+2)',
    currency: 'USD',
    title: {
      en: 'Software Development Agency Beirut | Call IT Dev',
      fr: 'Agence de Développement Logiciel Beyrouth | Call IT Dev',
      ar: 'وكالة تطوير البرمجيات بيروت | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Beirut. React, AI, cloud — enterprise quality at competitive rates.',
      fr: 'Agence de développement logiciel à Beyrouth. React, IA, cloud — qualité entreprise à prix compétitifs.',
      ar: 'وكالة تطوير برمجيات رائدة في بيروت. React، ذكاء اصطناعي، سحابة — جودة مؤسسية بأسعار تنافسية.',
    },
    keywords: ['software development Beirut', 'web development Lebanon', 'تطوير برمجيات بيروت', 'développement logiciel Beyrouth'],
    heroTitle: {
      en: 'Software Development Agency in Beirut',
      fr: 'Agence de Développement Logiciel à Beyrouth',
      ar: 'وكالة تطوير البرمجيات في بيروت',
    },
    heroSubtitle: {
      en: 'Enterprise software for Lebanese businesses. React, AI, cloud — trilingual teams (Arabic, French, English).',
      fr: 'Logiciels d\'entreprise pour le Liban. Équipes trilingues arabe, français, anglais.',
      ar: 'برمجيات مؤسسية للشركات اللبنانية. فرق ثلاثية اللغات: عربي، فرنسي، إنجليزي.',
    },
    whyTitle: {
      en: 'Why Beirut Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Beyrouth Choisissent Call IT Dev',
      ar: 'لماذا تختار شركات بيروت Call IT Dev',
    },
    whyReasons: [
      { icon: '🗣️', title: 'Trilingual Teams', description: 'Arabic, French, English — perfect cultural fit for Lebanese businesses.' },
      { icon: '💰', title: 'Cost Stability', description: 'USD-based pricing, no currency volatility.' },
      { icon: '⏰', title: 'Close Timezone', description: 'GMT+1 — only 1 hour from Beirut.' },
      { icon: '🛡️', title: 'Data Security', description: 'ISO 27001, GDPR compliant infrastructure.' },
      { icon: '🚀', title: 'Rapid Delivery', description: '2-week sprint cycles, CI/CD pipelines.' },
      { icon: '📈', title: 'Scalable', description: 'From 2 to 50+ developers in weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do your teams speak Arabic and French?', answer: 'Yes. Our teams are natively trilingual (Arabic, French, English), perfectly aligned with Lebanese business culture.' },
    ],
    localRate: '$25-50/h',
    ourRate: '$8-18/h',
    savings: '65%',
  },

  'amman': {
    slug: 'software-development-agency-amman',
    cityName: 'Amman',
    countryName: 'Jordan',
    flag: '🇯🇴',
    timezone: 'EET (GMT+2)',
    currency: 'JOD',
    title: {
      en: 'Software Development Agency Amman | Call IT Dev',
      ar: 'وكالة تطوير البرمجيات عمّان | Call IT Dev',
      fr: 'Agence de Développement Logiciel Amman | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Amman. React, AI, cloud — 55% savings vs Jordan rates.',
      ar: 'أفضل وكالة تطوير برمجيات في عمّان. React، ذكاء اصطناعي — وفر 55%.',
      fr: 'Agence de développement logiciel à Amman. React, IA, cloud — 55% d\'économies.',
    },
    keywords: ['software development Amman', 'web development Jordan', 'تطوير برمجيات عمان', 'app development Jordan'],
    heroTitle: {
      en: 'Software Development Agency in Amman',
      ar: 'وكالة تطوير البرمجيات في عمّان',
      fr: 'Agence de Développement Logiciel à Amman',
    },
    heroSubtitle: {
      en: 'Enterprise software for Jordanian businesses. Arabic-first teams, European quality, 55% savings.',
      ar: 'برمجيات مؤسسية للشركات الأردنية. فرق عربية، جودة أوروبية، وفر 55%.',
      fr: 'Logiciels d\'entreprise pour la Jordanie. Équipes arabophones, qualité européenne.',
    },
    whyTitle: {
      en: 'Why Amman Companies Choose Call IT Dev',
      ar: 'لماذا تختار شركات عمّان Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Amman Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🕌', title: 'Arabic Native', description: 'Native Arabic development teams.' },
      { icon: '💰', title: '55% Savings', description: 'Compared to Amman development rates.' },
      { icon: '⏰', title: 'Close Timezone', description: 'GMT+1 — 1 hour from Jordan.' },
      { icon: '🛡️', title: 'Compliance', description: 'ISO 27001, GDPR ready.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Agile sprints, 2-week cycles.' },
      { icon: '📈', title: 'Scale', description: 'Teams from 2 to 50+ developers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Can you support Arabic RTL interfaces?', answer: 'Absolutely. Our teams have extensive experience building Arabic RTL interfaces and bilingual Arabic-English applications.' },
    ],
    localRate: 'JOD 18-35/h',
    ourRate: 'JOD 8-16/h',
    savings: '55%',
  },

  'dar-es-salaam': {
    slug: 'software-development-agency-dar-es-salaam',
    cityName: 'Dar es Salaam',
    countryName: 'Tanzania',
    flag: '🇹🇿',
    timezone: 'EAT (GMT+3)',
    currency: 'TZS',
    title: {
      en: 'Software Development Agency Dar es Salaam | Call IT Dev',
      fr: 'Agence de Développement Logiciel Dar es Salaam | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Dar es Salaam. React, AI, mobile — enterprise quality for East Africa.',
      fr: 'Agence de développement logiciel à Dar es Salaam. React, IA, mobile — qualité entreprise pour l\'Afrique de l\'Est.',
    },
    keywords: ['software development Dar es Salaam', 'web development Tanzania', 'app development Tanzania'],
    heroTitle: {
      en: 'Software Development Agency in Dar es Salaam',
      fr: 'Agence de Développement Logiciel à Dar es Salaam',
    },
    heroSubtitle: {
      en: 'Enterprise software for East African businesses. React, AI, mobile money integrations.',
      fr: 'Logiciels d\'entreprise pour l\'Afrique de l\'Est. React, IA, intégrations mobile money.',
    },
    whyTitle: {
      en: 'Why Dar es Salaam Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Dar es Salaam Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'East Africa Focus', description: 'Deep understanding of East African markets.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Enterprise quality at nearshore prices.' },
      { icon: '📱', title: 'Mobile First', description: 'Mobile money, M-Pesa, fintech expertise.' },
      { icon: '🗣️', title: 'English + French', description: 'Bilingual teams for regional expansion.' },
      { icon: '⏰', title: 'Close Timezone', description: 'GMT+3 — 2 hours from Morocco.' },
      { icon: '📈', title: 'Scale', description: 'Ramp teams in 2-3 weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have experience with mobile money platforms?', answer: 'Yes. We have built M-Pesa integrations, mobile banking apps, and fintech platforms for the East African market.' },
    ],
    localRate: 'TZS 80,000-160,000/h',
    ourRate: 'TZS 35,000-75,000/h',
    savings: '55%',
  },

  'kigali': {
    slug: 'software-development-agency-kigali',
    cityName: 'Kigali',
    countryName: 'Rwanda',
    flag: '🇷🇼',
    timezone: 'CAT (GMT+2)',
    currency: 'RWF',
    title: {
      en: 'Software Development Agency Kigali | Call IT Dev',
      fr: 'Agence de Développement Logiciel Kigali | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Kigali. React, AI, cloud — supporting Rwanda\'s digital transformation.',
      fr: 'Agence de développement logiciel à Kigali. React, IA, cloud — accompagner la transformation digitale du Rwanda.',
    },
    keywords: ['software development Kigali', 'web development Rwanda', 'app development Kigali', 'développement logiciel Kigali'],
    heroTitle: {
      en: 'Software Development Agency in Kigali',
      fr: 'Agence de Développement Logiciel à Kigali',
    },
    heroSubtitle: {
      en: 'Enterprise software for Rwanda\'s innovation hub. React, AI, cloud — African excellence meets European quality.',
      fr: 'Logiciels d\'entreprise pour le hub d\'innovation du Rwanda. React, IA, cloud.',
    },
    whyTitle: {
      en: 'Why Kigali Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Kigali Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'Africa\'s Tech Hub', description: 'Supporting Rwanda\'s Vision 2050 digital agenda.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Enterprise quality at nearshore prices.' },
      { icon: '🗣️', title: 'French + English', description: 'Bilingual teams matching Rwanda\'s languages.' },
      { icon: '⏰', title: 'Close Timezone', description: 'GMT+2 — 1 hour from Morocco.' },
      { icon: '🛡️', title: 'Data Protection', description: 'GDPR aligned, enterprise security standards.' },
      { icon: '📈', title: 'Innovation', description: 'AI, blockchain, and emerging tech expertise.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Can you support Rwanda\'s digital transformation projects?', answer: 'Yes. We specialize in e-government, fintech, healthtech, and edtech — key pillars of Rwanda\'s Vision 2050.' },
    ],
    localRate: 'RWF 25,000-50,000/h',
    ourRate: 'RWF 12,000-25,000/h',
    savings: '50%',
  },

  'abidjan': {
    slug: 'software-development-agency-abidjan',
    cityName: 'Abidjan',
    countryName: 'Côte d\'Ivoire',
    flag: '🇨🇮',
    timezone: 'GMT (GMT+0)',
    currency: 'XOF',
    title: {
      en: 'Software Development Agency Abidjan | Call IT Dev',
      fr: 'Agence de Développement Logiciel Abidjan | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Abidjan. React, AI, cloud — French-speaking teams, competitive rates.',
      fr: 'Agence de développement logiciel à Abidjan. React, IA, cloud — équipes francophones, tarifs compétitifs.',
    },
    keywords: ['software development Abidjan', 'développement logiciel Abidjan', 'web development Côte d\'Ivoire', 'agence développement Abidjan'],
    heroTitle: {
      en: 'Software Development Agency in Abidjan',
      fr: 'Agence de Développement Logiciel à Abidjan',
    },
    heroSubtitle: {
      en: 'Enterprise software for West Africa\'s economic capital. French-speaking teams, European quality.',
      fr: 'Logiciels d\'entreprise pour la capitale économique d\'Afrique de l\'Ouest. Équipes francophones, qualité européenne.',
    },
    whyTitle: {
      en: 'Why Abidjan Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises d\'Abidjan Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🇫🇷', title: 'French Native', description: 'Perfect French communication, shared francophone culture.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Enterprise quality at nearshore prices.' },
      { icon: '⏰', title: 'Same Timezone', description: 'GMT+0 — 1 hour from Morocco.' },
      { icon: '🌍', title: 'UEMOA Expertise', description: 'Understanding of West African regulatory environment.' },
      { icon: '📱', title: 'Mobile Money', description: 'Orange Money, MTN integrations.' },
      { icon: '📈', title: 'Scale', description: 'Teams from 2 to 50+ developers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you understand the West African fintech ecosystem?', answer: 'Yes. We have built mobile money platforms, Orange Money integrations, and banking apps for the UEMOA region.' },
    ],
    localRate: 'XOF 30,000-60,000/h',
    ourRate: 'XOF 15,000-30,000/h',
    savings: '50%',
  },

  'dakar': {
    slug: 'software-development-agency-dakar',
    cityName: 'Dakar',
    countryName: 'Senegal',
    flag: '🇸🇳',
    timezone: 'GMT (GMT+0)',
    currency: 'XOF',
    title: {
      en: 'Software Development Agency Dakar | Call IT Dev',
      fr: 'Agence de Développement Logiciel Dakar | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Dakar. React, AI, cloud — French-speaking teams, competitive rates.',
      fr: 'Agence de développement logiciel à Dakar. React, IA, cloud — équipes francophones, tarifs compétitifs.',
    },
    keywords: ['software development Dakar', 'développement logiciel Dakar', 'web development Senegal', 'agence développement Dakar'],
    heroTitle: {
      en: 'Software Development Agency in Dakar',
      fr: 'Agence de Développement Logiciel à Dakar',
    },
    heroSubtitle: {
      en: 'Enterprise software for Senegal\'s tech ecosystem. French-speaking teams, European expertise.',
      fr: 'Logiciels d\'entreprise pour l\'écosystème tech sénégalais. Équipes francophones, expertise européenne.',
    },
    whyTitle: {
      en: 'Why Dakar Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Dakar Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🇫🇷', title: 'Francophone', description: 'Native French teams, shared cultural context.' },
      { icon: '💰', title: 'Competitive Rates', description: 'Enterprise quality at nearshore prices.' },
      { icon: '⏰', title: 'Same Timezone', description: 'GMT+0 — 1 hour from Morocco.' },
      { icon: '🌍', title: 'West Africa', description: 'Understanding of Senegalese and WAEMU markets.' },
      { icon: '📱', title: 'Mobile First', description: 'Wave, Orange Money, fintech expertise.' },
      { icon: '📈', title: 'Scale', description: 'Rapid team scaling for any project size.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Can you build fintech apps for the Senegalese market?', answer: 'Yes. We have experience with Wave, Orange Money APIs, and BCEAO-compliant financial applications.' },
    ],
    localRate: 'XOF 25,000-50,000/h',
    ourRate: 'XOF 12,000-25,000/h',
    savings: '50%',
  },

  'bangalore': {
    slug: 'software-development-agency-bangalore',
    cityName: 'Bangalore',
    countryName: 'India',
    flag: '🇮🇳',
    timezone: 'IST (GMT+5:30)',
    currency: 'INR',
    title: {
      en: 'Software Development Agency Bangalore | Call IT Dev',
      fr: 'Agence de Développement Logiciel Bangalore | Call IT Dev',
      de: 'Softwareentwicklungsagentur Bangalore | Call IT Dev',
      ja: 'ソフトウェア開発エージェンシー バンガロール | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Bangalore. React, AI, cloud — European quality, follow-the-sun development.',
      fr: 'Agence de développement logiciel pour Bangalore. React, IA, cloud — qualité européenne.',
      de: 'Softwareentwicklungsagentur für Bangalore. React, KI, Cloud — europäische Qualität.',
      ja: 'バンガロールのトップソフトウェア開発エージェンシー。React、AI、クラウド。',
    },
    keywords: ['software development Bangalore', 'web development India', 'React developers Bangalore', 'app development Bangalore'],
    heroTitle: {
      en: 'Software Development Agency in Bangalore',
      fr: 'Agence de Développement Logiciel à Bangalore',
      de: 'Softwareentwicklungsagentur in Bangalore',
      ja: 'バンガロールのソフトウェア開発エージェンシー',
    },
    heroSubtitle: {
      en: 'Enterprise software for India\'s Silicon Valley. React, AI, cloud — European expertise with follow-the-sun coverage.',
      fr: 'Logiciels d\'entreprise pour la Silicon Valley indienne. React, IA, cloud — expertise européenne.',
      de: 'Enterprise-Software für Indiens Silicon Valley. React, KI, Cloud — europäische Expertise.',
      ja: 'インドのシリコンバレー向けエンタープライズソフトウェア。',
    },
    whyTitle: {
      en: 'Why Bangalore Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Bangalore Choisissent Call IT Dev',
      de: 'Warum Unternehmen in Bangalore Call IT Dev wählen',
      ja: 'バンガロールの企業がCall IT Devを選ぶ理由',
    },
    whyReasons: [
      { icon: '🌍', title: 'European Languages', description: 'Native EU language support for global expansion.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'GMT+1 complements IST for 24/7 development.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'EU-compliant for European market entry.' },
      { icon: '💰', title: 'Competitive', description: 'Enterprise quality at nearshore rates.' },
      { icon: '🗣️', title: 'English Native', description: 'Perfect English communication.' },
      { icon: '📈', title: 'Scale', description: '500+ developers available.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'How does the timezone difference work with Bangalore?', answer: 'GMT+1 and IST (GMT+5:30) create excellent follow-the-sun coverage with 4.5 hours overlap for daily standups.' },
    ],
    localRate: 'INR 3,000-6,000/h',
    ourRate: 'INR 1,500-3,000/h',
    savings: '50%',
  },

  'jakarta': {
    slug: 'software-development-agency-jakarta',
    cityName: 'Jakarta',
    countryName: 'Indonesia',
    flag: '🇮🇩',
    timezone: 'WIB (GMT+7)',
    currency: 'IDR',
    title: {
      en: 'Software Development Agency Jakarta | Call IT Dev',
      fr: 'Agence de Développement Logiciel Jakarta | Call IT Dev',
      ja: 'ソフトウェア開発エージェンシー ジャカルタ | Call IT Dev',
      ko: '소프트웨어 개발 에이전시 자카르타 | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Jakarta. React, AI, cloud — European quality for Southeast Asia.',
      fr: 'Agence de développement logiciel à Jakarta. React, IA, cloud — qualité européenne pour l\'Asie du Sud-Est.',
      ja: 'ジャカルタのトップソフトウェア開発エージェンシー。React、AI、クラウド。',
      ko: '자카르타 최고의 소프트웨어 개발 에이전시. React, AI, 클라우드.',
    },
    keywords: ['software development Jakarta', 'web development Indonesia', 'app development Jakarta', 'React developers Indonesia'],
    heroTitle: {
      en: 'Software Development Agency in Jakarta',
      fr: 'Agence de Développement Logiciel à Jakarta',
      ja: 'ジャカルタのソフトウェア開発エージェンシー',
      ko: '자카르타의 소프트웨어 개발 에이전시',
    },
    heroSubtitle: {
      en: 'Enterprise software for Indonesia\'s digital economy. React, AI, cloud — European expertise.',
      fr: 'Logiciels d\'entreprise pour l\'économie numérique indonésienne. React, IA, cloud.',
      ja: 'インドネシアのデジタル経済向けエンタープライズソフトウェア。',
      ko: '인도네시아 디지털 경제를 위한 엔터프라이즈 소프트웨어.',
    },
    whyTitle: {
      en: 'Why Jakarta Companies Choose Call IT Dev',
      fr: 'Pourquoi les Entreprises de Jakarta Choisissent Call IT Dev',
      ja: 'ジャカルタの企業がCall IT Devを選ぶ理由',
      ko: '자카르타 기업이 Call IT Dev를 선택하는 이유',
    },
    whyReasons: [
      { icon: '🌍', title: 'EU Market Access', description: 'Native European languages for global expansion.' },
      { icon: '⏰', title: '24/7 Coverage', description: 'GMT+1 complements GMT+7 for round-the-clock dev.' },
      { icon: '🛡️', title: 'GDPR Compliant', description: 'EU data protection for international operations.' },
      { icon: '💰', title: 'Competitive', description: 'Enterprise quality at nearshore rates.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Agile sprints, 2-week cycles.' },
      { icon: '📈', title: 'Scalable', description: 'Teams from 2 to 50+ developers.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Can you help Indonesian companies enter European markets?', answer: 'Yes. We provide native EU language capabilities, GDPR compliance, and European market expertise for Indonesian businesses expanding globally.' },
    ],
    localRate: 'IDR 500,000-1,000,000/h',
    ourRate: 'IDR 250,000-500,000/h',
    savings: '50%',
  },

  'santiago': {
    slug: 'software-development-agency-santiago',
    cityName: 'Santiago',
    countryName: 'Chile',
    flag: '🇨🇱',
    timezone: 'CLT (GMT-4)',
    currency: 'CLP',
    title: {
      en: 'Software Development Agency Santiago | Call IT Dev',
      es: 'Agencia de Desarrollo de Software Santiago | Call IT Dev',
      fr: 'Agence de Développement Logiciel Santiago | Call IT Dev',
      pt: 'Agência de Desenvolvimento de Software Santiago | Call IT Dev',
    },
    description: {
      en: 'Top software development agency for Santiago. React, AI, cloud — 50% savings vs Chilean rates.',
      es: 'Agencia líder de desarrollo de software en Santiago. React, IA, nube — 50% ahorro vs tarifas locales.',
      fr: 'Agence de développement logiciel à Santiago. React, IA, cloud — 50% d\'économies.',
      pt: 'Agência de desenvolvimento de software em Santiago. React, IA, nuvem — 50% economia.',
    },
    keywords: ['software development Santiago', 'desarrollo software Santiago', 'web development Chile', 'React developers Chile'],
    heroTitle: {
      en: 'Software Development Agency in Santiago',
      es: 'Agencia de Desarrollo de Software en Santiago',
      fr: 'Agence de Développement Logiciel à Santiago',
      pt: 'Agência de Desenvolvimento de Software em Santiago',
    },
    heroSubtitle: {
      en: 'Enterprise software for Chile\'s tech ecosystem. React, AI, cloud — European quality at 50% savings.',
      es: 'Software empresarial para el ecosistema tech de Chile. React, IA, nube — calidad europea con 50% de ahorro.',
      fr: 'Logiciels d\'entreprise pour l\'écosystème tech chilien. React, IA, cloud — 50% d\'économies.',
      pt: 'Software empresarial para o ecossistema tech do Chile. React, IA — 50% de economia.',
    },
    whyTitle: {
      en: 'Why Santiago Companies Choose Call IT Dev',
      es: 'Por qué las Empresas de Santiago Eligen Call IT Dev',
      fr: 'Pourquoi les Entreprises de Santiago Choisissent Call IT Dev',
      pt: 'Por que Empresas de Santiago Escolhem a Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'European Languages', description: 'Native French, German, Arabic for EU/MENA expansion.' },
      { icon: '💰', title: '50% Savings', description: 'Compared to Santiago development rates.' },
      { icon: '🗣️', title: 'Spanish Native', description: 'Perfect Spanish + 12 other languages.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'EU-compliant for European market entry.' },
      { icon: '🚀', title: 'Agile', description: '2-week sprint cycles.' },
      { icon: '📈', title: 'Scale', description: 'From MVP to enterprise in weeks.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Can you help Chilean startups expand to Europe?', answer: 'Yes. We provide GDPR compliance, native EU languages, and European market expertise for Chilean tech companies.' },
    ],
    localRate: 'CLP 40,000-80,000/h',
    ourRate: 'CLP 20,000-40,000/h',
    savings: '50%',
  },

  'monterrey': {
    slug: 'software-development-agency-monterrey',
    cityName: 'Monterrey',
    countryName: 'Mexico',
    flag: '🇲🇽',
    timezone: 'CST (GMT-6)',
    currency: 'MXN',
    title: {
      en: 'Software Development Agency Monterrey | Call IT Dev',
      es: 'Agencia de Desarrollo de Software Monterrey | Call IT Dev',
      fr: 'Agence de Développement Logiciel Monterrey | Call IT Dev',
    },
    description: {
      en: 'Leading software development agency for Monterrey. React, AI, cloud — 55% savings vs local rates.',
      es: 'Agencia líder de desarrollo de software en Monterrey. React, IA, nube — 55% ahorro vs tarifas locales.',
      fr: 'Agence de développement logiciel à Monterrey. React, IA, cloud — 55% d\'économies.',
    },
    keywords: ['software development Monterrey', 'desarrollo software Monterrey', 'web development Monterrey', 'React developers Mexico'],
    heroTitle: {
      en: 'Software Development Agency in Monterrey',
      es: 'Agencia de Desarrollo de Software en Monterrey',
      fr: 'Agence de Développement Logiciel à Monterrey',
    },
    heroSubtitle: {
      en: 'Enterprise software for Mexico\'s industrial capital. React, AI, cloud — 55% savings vs Monterrey agencies.',
      es: 'Software empresarial para la capital industrial de México. React, IA, nube — 55% ahorro.',
      fr: 'Logiciels d\'entreprise pour la capitale industrielle du Mexique. 55% d\'économies.',
    },
    whyTitle: {
      en: 'Why Monterrey Companies Choose Call IT Dev',
      es: 'Por qué las Empresas de Monterrey Eligen Call IT Dev',
      fr: 'Pourquoi les Entreprises de Monterrey Choisissent Call IT Dev',
    },
    whyReasons: [
      { icon: '🌍', title: 'European Access', description: 'Native EU languages for global expansion.' },
      { icon: '💰', title: '55% Savings', description: 'Compared to Monterrey development rates.' },
      { icon: '🗣️', title: 'Spanish + 12 Languages', description: 'Native Spanish and European languages.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'EU-compliant for European market entry.' },
      { icon: '🚀', title: 'Industrial Expertise', description: 'Manufacturing, logistics, supply chain software.' },
      { icon: '📈', title: 'Scale', description: 'Rapid team scaling.' },
    ],
    services: sharedServices,
    industries: sharedIndustries,
    faqs: [
      { question: 'Do you have experience with manufacturing and supply chain software?', answer: 'Yes. We build ERP integrations, supply chain visibility platforms, and IoT solutions for industrial companies.' },
    ],
    localRate: 'MXN 800-1,600/h',
    ourRate: 'MXN 350-750/h',
    savings: '55%',
  },
};
